/**
 * Superfície de origem do Registrar (Bloco 4 §2 / §8 — source_surface).
 * REG-09: quando `contactId`/`contactName` vêm da tela e o relato cita outra pessoa,
 * `RegisterRelatoFlow` força esclarecimento antes do confirm (não rebinde silencioso).
 */
export type RegisterSourceSurface =
  | 'global'
  | 'cliente'
  | 'inbox'
  | 'oportunidade'
  | 'funil'
  | 'hoje'
  | 'central'
  | 'register'
  | 'unknown';

export type RegisterConversationOpenDetail = {
  contactId?: number;
  contactName?: string;
  conversationId?: number;
  messageId?: number;
  calendarEventId?: string;
  /** Oportunidade/funil quando disponível (sem inventar Opportunity entity). */
  opportunityId?: string | number;
  screen?: string;
  pathname?: string;
  /** Origem explícita; se omitida, inferida de screen/pathname. */
  sourceSurface?: RegisterSourceSurface;
  /** Funil: sempre qualified_contact quando há contactId. */
  subjectKind?: 'qualified_contact';
};

const EVENT = 'mends-register-open';

const SURFACE_LABEL: Record<RegisterSourceSurface, string> = {
  global: 'Menu global',
  cliente: 'Cliente',
  inbox: 'Inbox',
  oportunidade: 'Oportunidade',
  funil: 'Funil',
  hoje: 'Hoje',
  central: 'Central',
  register: 'Registrar',
  unknown: 'Tela atual',
};

export function sourceSurfaceLabel(surface?: RegisterSourceSurface | string | null): string {
  if (!surface) return SURFACE_LABEL.unknown;
  return SURFACE_LABEL[surface as RegisterSourceSurface] || String(surface);
}

export function inferSourceSurface(
  screen?: string,
  pathname?: string,
): RegisterSourceSurface {
  const s = (screen || '').toLowerCase();
  const p = (pathname || '').toLowerCase();
  if (s === 'chat' || p.startsWith('/chat')) return 'inbox';
  if (s === 'contacts' || p.startsWith('/contacts') || p.startsWith('/contatos')) return 'cliente';
  if (s === 'opportunities' || p.startsWith('/oportunidades') || p.startsWith('/opportunities')) {
    return 'oportunidade';
  }
  if (s === 'funnel' || p.startsWith('/crm') || p.includes('funil') || p.includes('kanban')) return 'funil';
  if (s === 'hoje' || p.startsWith('/hoje')) return 'hoje';
  if (s === 'central' || s === 'approvals' || p.startsWith('/central-inteligencia')) return 'central';
  if (s === 'register' || p.startsWith('/registrar')) return 'register';
  if (!s || s === 'unknown') return 'global';
  return 'unknown';
}

export function buildRegisterScreenContext(
  detail?: RegisterConversationOpenDetail,
): RegisterConversationOpenDetail {
  const pathname = detail?.pathname
    || (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const screen = detail?.screen || inferScreen(pathname, search);
  const contactId = detail?.contactId;
  const sourceSurface = detail?.sourceSurface || inferSourceSurface(screen, pathname);
  return {
    ...detail,
    pathname: sanitizePath(pathname),
    screen,
    sourceSurface,
    subjectKind: contactId ? 'qualified_contact' : detail?.subjectKind,
  };
}

export function openRegisterConversation(detail?: RegisterConversationOpenDetail) {
  window.dispatchEvent(
    new CustomEvent(EVENT, { detail: buildRegisterScreenContext(detail || {}) }),
  );
}

export function subscribeRegisterConversation(
  handler: (detail: RegisterConversationOpenDetail) => void,
): () => void {
  const listener = (e: Event) => {
    handler((e as CustomEvent<RegisterConversationOpenDetail>).detail || {});
  };
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

function sanitizePath(pathname?: string): string | undefined {
  if (!pathname) return undefined;
  const q = pathname.indexOf('?');
  const h = pathname.indexOf('#');
  let p = pathname;
  if (q >= 0) p = p.slice(0, q);
  if (h >= 0) p = p.slice(0, h);
  return p.slice(0, 200);
}

function inferScreen(pathname?: string, search = ''): string | undefined {
  if (!pathname) return undefined;
  const p = pathname.toLowerCase();
  const q = (search || '').toLowerCase();
  if (p.startsWith('/chat')) return 'chat';
  if (p.startsWith('/oportunidades') || p.startsWith('/opportunities')) return 'opportunities';
  if (p.startsWith('/contacts') || p.startsWith('/contatos')) return 'contacts';
  if (p.startsWith('/crm') || p.includes('funil') || p.includes('kanban')) return 'funnel';
  if (p.startsWith('/central-inteligencia')) {
    if (q.includes('tab=aprovacoes') || q.includes('tab=approvals')) return 'approvals';
    if (q.includes('tab=oportunidades') || q.includes('tab=opportunities')) return 'opportunities';
    return 'central';
  }
  if (p.startsWith('/hoje')) return 'hoje';
  if (p.startsWith('/registrar')) return 'register';
  if (p.startsWith('/agenda') || p.startsWith('/schedule')) return 'agenda';
  if (p.startsWith('/aprovacoes')) return 'approvals';
  return 'unknown';
}
